import { IWFNodes, IWfBounds, IWfDocument, IWfElement, IWfPoint, IWorkFlow } from "@kuika/kuika-cl-model";

/**
 * Pure extractor: live bpmn-js model (elementRegistry + moddle businessObjects) merged with
 * the semantic side-car (`selectedWf.nodes`) → the flat wf-patch IWfDocument the collab
 * session core diffs and applies.
 *
 * The produced doc shape is the CROSS-LANGUAGE CONTRACT pinned by the shared op-apply
 * fixtures (kuika-cl-model/src/utilities/wf-patch/__fixtures__/op-apply) and by the C#
 * WfWireCodec/WfNormalizedModel conventions:
 *  - participant / process / collaboration are NOT elements-map entries; lanes ARE
 *    (bpmnType "bpmn:Lane");
 *  - `containerId` is "" for root-process children, else the owning bpmn:SubProcess id
 *    (lane "parents" are skipped — lane membership is the server-derived `laneId`);
 *  - `name` is TOP-LEVEL on the element record and `semantics` NEVER carries `name` (nor
 *    any other derived key — id/nodeType plus the per-category derived fields, exactly
 *    mirroring the C# StripDerivedSemanticKeys);
 *  - per-container `elementOrder` buckets list LANES FIRST, then flow elements, in
 *    registry/container traversal order (matching XML emission where the laneSet precedes
 *    the flow elements);
 *  - geometry comes ONLY from the registry model (shape x/y/width/height, connection
 *    waypoints, external-label shape bounds) — this module never touches the DOM.
 *
 * Passthrough XML fields (eventDefinitionXml / extensionElementsXml / documentationXml /
 * conditionExpressionXml) are re-serialized from the moddle businessObject with a small
 * deterministic writer. Fragments always use the `bpmn:` prefix and self-declare every
 * namespace they use — the server compares XML passthroughs namespace-aware
 * (XNode.DeepEquals), so prefix choice is irrelevant, and both collab clients run this
 * same writer, so client↔client diffs are formatting-stable by construction.
 *
 * Known v1 limitation: elements outside the closed BPMN vocabulary (which the palette
 * cannot produce) are excluded from the doc — they stay on the local canvas but do not
 * replicate. The server rejects such content at join-seed time (seedRejected), so a live
 * session can only meet one via an exotic local paste.
 */

// ---- narrow structural views of the bpmn-js surfaces (keeps this module pure/testable) ----

export interface IWfModdleBusinessObject {
  id?: string;
  $type?: string;
  name?: string;
  sourceRef?: IWfModdleBusinessObject;
  targetRef?: IWfModdleBusinessObject;
  attachedToRef?: IWfModdleBusinessObject;
  processRef?: IWfModdleBusinessObject;
  cancelActivity?: boolean;
  default?: IWfModdleBusinessObject;
  eventDefinitions?: IWfModdleAny[];
  extensionElements?: IWfModdleExtensionElements;
  documentation?: IWfModdleAny[];
  conditionExpression?: IWfModdleAny;
  flowNodeRef?: IWfModdleBusinessObject[];
  /** bpmn:Group label companion — the rendered label is categoryValueRef.value. */
  categoryValueRef?: IWfCategoryValueLike;
  /** Happy-path phase markers (custom.json moddle extension on bpmn:Group). */
  isHappyPathPhase?: boolean;
  phaseId?: string;
  $attrs?: Record<string, string>;
}

export interface IWfCategoryValueLike {
  id?: string;
  value?: string;
}

export interface IWfModdleExtensionElements {
  values?: IWfModdleAny[];
}

/** A moddle element treated generically (event definitions, extension bodies, docs). */
export interface IWfModdleAny {
  $type?: string;
  id?: string;
  body?: string;
  text?: string;
  textFormat?: string;
  language?: string;
  timeDate?: IWfModdleAny;
  timeDuration?: IWfModdleAny;
  timeCycle?: IWfModdleAny;
  $children?: IWfModdleAny[];
  $body?: string;
  $attrs?: Record<string, string>;
  [key: string]: unknown;
}

export interface IWfRegistryDiLabel {
  id?: string;
  bounds?: IWfBounds;
}

export interface IWfRegistryDi {
  id?: string;
  isExpanded?: boolean;
  isHorizontal?: boolean;
  isMarkerVisible?: boolean;
  label?: IWfRegistryDiLabel;
}

export interface IWfRegistryElement {
  id: string;
  /** diagram-js element type: "bpmn:UserTask", "bpmn:SequenceFlow", "label", ... */
  type: string;
  businessObject?: IWfModdleBusinessObject;
  di?: IWfRegistryDi;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  waypoints?: IWfPoint[];
  parent?: IWfRegistryElement;
  host?: IWfRegistryElement;
  source?: IWfRegistryElement;
  target?: IWfRegistryElement;
  /** External label shape (events/gateways/flows with a rendered name). */
  label?: IWfRegistryElement;
  labelTarget?: IWfRegistryElement;
}

export interface IWfElementRegistryLike {
  getAll(): IWfRegistryElement[];
}

export interface IWfDefinitionsLike {
  $attrs?: Record<string, string>;
  rootElements?: IWfModdleBusinessObject[];
}

export interface IWfModelerLike {
  get(name: string): unknown;
  getDefinitions?(): IWfDefinitionsLike | undefined;
}

// ---- closed vocabulary (mirror of C# WfBpmnTypes) ------------------------------------------

const WF_STEP_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:Task",
  "bpmn:UserTask",
  "bpmn:SendTask",
  "bpmn:SubProcess",
  "bpmn:CallActivity"
]);

const WF_GATEWAY_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:ExclusiveGateway",
  "bpmn:ParallelGateway",
  "bpmn:InclusiveGateway"
]);

const WF_STANDALONE_EVENT_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:StartEvent",
  "bpmn:EndEvent",
  "bpmn:IntermediateCatchEvent"
]);

export const WF_SUPPORTED_BPMN_TYPES: ReadonlySet<string> = new Set<string>([
  ...WF_STEP_TYPES,
  ...WF_GATEWAY_TYPES,
  ...WF_STANDALONE_EVENT_TYPES,
  "bpmn:BoundaryEvent",
  "bpmn:SequenceFlow",
  "bpmn:Lane",
  // Artifact, root-only, never a lane member — happy-path phases persist as groups
  // (KOA-15840); leaving them out of the contract made every phased process seedRejected.
  "bpmn:Group"
]);

/** Namespace URI of the custom.json moddle extension (custom:isHappyPathPhase / custom:phaseId). */
const CUSTOM_MODDLE_NS: string = "http://custom/ns";

const SKIPPED_CONTAINER_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:Process",
  "bpmn:Collaboration",
  "bpmn:Participant"
]);

/** Derived semantic keys stripped per category — exact mirror of WfWireCodec.StripDerivedSemanticKeys. */
const BASE_DERIVED_KEYS: readonly string[] = ["id", "name", "nodeType"];
const STEP_DERIVED_KEYS: readonly string[] = ["stepType", "boundaryEvent", "subprocess"];
const BOUNDARY_DERIVED_KEYS: readonly string[] = ["eventType", "eventTag", "isInterrupting"];
const EVENT_DERIVED_KEYS: readonly string[] = ["eventType", "eventTag"];
const GATEWAY_DERIVED_KEYS: readonly string[] = ["gateWayType"];
const FLOW_DERIVED_KEYS: readonly string[] = ["arrowType", "fromNodeId", "toNodeId", "isDefault"];

// ---- namespaces -----------------------------------------------------------------------------

const BPMN_MODEL_NS: string = "http://www.omg.org/spec/BPMN/20100524/MODEL";
const XSI_NS: string = "http://www.w3.org/2001/XMLSchema-instance";

interface IWfXmlNode {
  /** Prefixed element name, e.g. "bpmn:timerEventDefinition" or "custom:props". */
  name: string;
  attrs: [string, string][];
  text?: string;
  children: IWfXmlNode[];
}

function escapeXmlText(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeXmlAttr(value: string): string {
  return escapeXmlText(value).replace(/"/g, "&quot;");
}

function prefixOf(name: string): string | null {
  const colon: number = name.indexOf(":");
  if (colon <= 0) return null;
  return name.substring(0, colon);
}

function collectPrefixes(node: IWfXmlNode, into: Set<string>): void {
  const namePrefix: string | null = prefixOf(node.name);
  if (namePrefix) into.add(namePrefix);
  for (const [attrName] of node.attrs) {
    const attrPrefix: string | null = prefixOf(attrName);
    if (attrPrefix && attrPrefix !== "xmlns") into.add(attrPrefix);
  }
  for (const child of node.children) {
    collectPrefixes(child, into);
  }
}

function stringifyNode(node: IWfXmlNode): string {
  const attrText: string = node.attrs
    .map(([name, value]: [string, string]) => ` ${name}="${escapeXmlAttr(value)}"`)
    .join("");
  const childText: string = node.children.map(stringifyNode).join("");
  const body: string = (node.text !== undefined ? escapeXmlText(node.text) : "") + childText;
  if (body === "") {
    return `<${node.name}${attrText} />`;
  }
  return `<${node.name}${attrText}>${body}</${node.name}>`;
}

/**
 * Serializes a fragment, self-declaring every namespace prefix it uses on the root element.
 * Prefixes without a known URI make the fragment unserializable → null (caller omits the
 * field rather than emitting XML the server would reject as malformed).
 */
function serializeFragment(root: IWfXmlNode, prefixToUri: ReadonlyMap<string, string>): string | null {
  const prefixes: Set<string> = new Set<string>();
  collectPrefixes(root, prefixes);
  const declarations: [string, string][] = [];
  for (const prefix of [...prefixes].sort()) {
    const uri: string | undefined = prefixToUri.get(prefix);
    if (uri === undefined) return null;
    declarations.push([`xmlns:${prefix}`, uri]);
  }
  const rooted: IWfXmlNode = { ...root, attrs: [...root.attrs, ...declarations] };
  return stringifyNode(rooted);
}

/** xmlns prefix → URI map: bpmn/xsi built-ins plus every xmlns:* on the definitions root. */
function buildPrefixMap(definitions: IWfDefinitionsLike | undefined): Map<string, string> {
  const map: Map<string, string> = new Map<string, string>();
  map.set("bpmn", BPMN_MODEL_NS);
  map.set("xsi", XSI_NS);
  const attrs: Record<string, string> = definitions?.$attrs ?? {};
  for (const key of Object.keys(attrs)) {
    if (key.startsWith("xmlns:")) {
      map.set(key.substring("xmlns:".length), attrs[key]);
    }
  }
  return map;
}

// ---- moddle → fragment builders ---------------------------------------------------------------

/** "bpmn:TimerEventDefinition" → "bpmn:timerEventDefinition" (BPMN XML lowerCamel local names). */
function bpmnXmlName(moddleType: string): string {
  const colon: number = moddleType.indexOf(":");
  if (colon < 0) return moddleType;
  const prefix: string = moddleType.substring(0, colon);
  const local: string = moddleType.substring(colon + 1);
  return `${prefix}:${local.charAt(0).toLowerCase()}${local.substring(1)}`;
}

function formalExpressionChild(name: string, expression: IWfModdleAny): IWfXmlNode {
  const attrs: [string, string][] = [["xsi:type", "bpmn:tFormalExpression"]];
  if (typeof expression.id === "string") attrs.push(["id", expression.id]);
  if (typeof expression.language === "string") attrs.push(["language", expression.language]);
  return { name, attrs, text: typeof expression.body === "string" ? expression.body : "", children: [] };
}

/** Generic serializer for moddle "any" elements (extensionElements bodies). */
function anyElementToNode(el: IWfModdleAny): IWfXmlNode | null {
  const typeName: string = typeof el.$type === "string" ? el.$type : "";
  if (typeName === "") return null;
  const attrs: [string, string][] = [];
  for (const key of Object.keys(el)) {
    if (key.startsWith("$")) continue;
    const value: unknown = el[key];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      attrs.push([key, String(value)]);
    }
  }
  const children: IWfXmlNode[] = [];
  for (const child of el.$children ?? []) {
    const childNode: IWfXmlNode | null = anyElementToNode(child);
    if (childNode) children.push(childNode);
  }
  return { name: typeName, attrs, text: typeof el.$body === "string" ? el.$body : undefined, children };
}

interface IWfEventDefinitionParts {
  kind: string;
  xml: string;
}

function extractEventDefinition(
  bo: IWfModdleBusinessObject,
  prefixToUri: ReadonlyMap<string, string>
): IWfEventDefinitionParts | null {
  const definition: IWfModdleAny | undefined = bo.eventDefinitions?.[0];
  if (!definition || typeof definition.$type !== "string") return null;
  let kind: string;
  if (definition.$type === "bpmn:TimerEventDefinition") {
    kind = "timer";
  } else if (definition.$type === "bpmn:MessageEventDefinition") {
    kind = "message";
  } else if (definition.$type === "bpmn:SignalEventDefinition") {
    kind = "signal";
  } else {
    return null;
  }
  const attrs: [string, string][] = [];
  if (typeof definition.id === "string") attrs.push(["id", definition.id]);
  const children: IWfXmlNode[] = [];
  if (definition.timeDate) children.push(formalExpressionChild("bpmn:timeDate", definition.timeDate));
  if (definition.timeDuration) children.push(formalExpressionChild("bpmn:timeDuration", definition.timeDuration));
  if (definition.timeCycle) children.push(formalExpressionChild("bpmn:timeCycle", definition.timeCycle));
  const root: IWfXmlNode = { name: bpmnXmlName(definition.$type), attrs, children };
  const xml: string | null = serializeFragment(root, prefixToUri);
  if (xml === null) return null;
  return { kind, xml };
}

function extractExtensionElementsXml(
  bo: IWfModdleBusinessObject,
  prefixToUri: ReadonlyMap<string, string>
): string | undefined {
  const values: IWfModdleAny[] = bo.extensionElements?.values ?? [];
  if (values.length === 0) return undefined;
  const children: IWfXmlNode[] = [];
  for (const value of values) {
    const child: IWfXmlNode | null = anyElementToNode(value);
    if (child) children.push(child);
  }
  const root: IWfXmlNode = { name: "bpmn:extensionElements", attrs: [], children };
  return serializeFragment(root, prefixToUri) ?? undefined;
}

function extractDocumentationXml(
  bo: IWfModdleBusinessObject,
  prefixToUri: ReadonlyMap<string, string>
): string[] | undefined {
  const entries: IWfModdleAny[] = bo.documentation ?? [];
  if (entries.length === 0) return undefined;
  const result: string[] = [];
  for (const entry of entries) {
    const attrs: [string, string][] = [];
    if (typeof entry.id === "string") attrs.push(["id", entry.id]);
    if (typeof entry.textFormat === "string") attrs.push(["textFormat", entry.textFormat]);
    const node: IWfXmlNode = {
      name: "bpmn:documentation",
      attrs,
      text: typeof entry.text === "string" ? entry.text : "",
      children: []
    };
    const xml: string | null = serializeFragment(node, prefixToUri);
    if (xml !== null) result.push(xml);
  }
  return result.length > 0 ? result : undefined;
}

function extractConditionExpressionXml(
  bo: IWfModdleBusinessObject,
  prefixToUri: ReadonlyMap<string, string>
): string | undefined {
  const expression: IWfModdleAny | undefined = bo.conditionExpression;
  if (!expression) return undefined;
  const node: IWfXmlNode = formalExpressionChild("bpmn:conditionExpression", expression);
  return serializeFragment(node, prefixToUri) ?? undefined;
}

interface IWfNsAttrOut {
  ns: string;
  local: string;
  value: string;
}

/** Namespaced custom attributes (custom:topic) — prefix resolved against the definitions xmlns map. */
function extractCustomAttrs(
  bo: IWfModdleBusinessObject,
  prefixToUri: ReadonlyMap<string, string>
): IWfNsAttrOut[] | undefined {
  const raw: Record<string, string> = bo.$attrs ?? {};
  const result: IWfNsAttrOut[] = [];
  for (const key of Object.keys(raw)) {
    const colon: number = key.indexOf(":");
    if (colon <= 0) continue;
    const prefix: string = key.substring(0, colon);
    if (prefix === "xmlns" || prefix === "xsi") continue;
    const uri: string | undefined = prefixToUri.get(prefix);
    if (uri === undefined) continue;
    result.push({ ns: uri, local: key.substring(colon + 1), value: String(raw[key]) });
  }
  return result.length > 0 ? result : undefined;
}

// ---- semantic side-car flatten ------------------------------------------------------------------

interface IWfSemanticEntry {
  node: Record<string, unknown>;
}

interface IWfNodesLike {
  steps?: Record<string, unknown>[];
  events?: Record<string, unknown>[];
  gateways?: Record<string, unknown>[];
  lanes?: Record<string, unknown>[];
  forwards?: Record<string, unknown>[];
  decisions?: Record<string, unknown>[];
}

/** id → semantic node, flattening subprocesses and hoisting boundary events to their own id. */
function flattenSemanticNodes(nodes: IWFNodes | undefined): Map<string, IWfSemanticEntry> {
  const map: Map<string, IWfSemanticEntry> = new Map<string, IWfSemanticEntry>();
  const walk = (bag: IWfNodesLike | undefined): void => {
    if (!bag) return;
    const lists: (Record<string, unknown>[] | undefined)[] = [
      bag.steps,
      bag.events,
      bag.gateways,
      bag.lanes,
      bag.forwards,
      bag.decisions
    ];
    for (const list of lists) {
      if (!Array.isArray(list)) continue;
      for (const node of list) {
        const id: unknown = node?.id;
        if (typeof id === "string" && id !== "" && !map.has(id)) {
          map.set(id, { node });
        }
      }
    }
    for (const step of bag.steps ?? []) {
      const boundary: unknown = step?.boundaryEvent;
      if (boundary && typeof boundary === "object") {
        const boundaryNode: Record<string, unknown> = boundary as Record<string, unknown>;
        const boundaryId: unknown = boundaryNode.id;
        if (typeof boundaryId === "string" && boundaryId !== "" && !map.has(boundaryId)) {
          map.set(boundaryId, { node: boundaryNode });
        }
      }
      const subprocess: unknown = step?.subprocess;
      if (subprocess && typeof subprocess === "object") {
        walk(subprocess as IWfNodesLike);
      }
    }
  };
  walk(nodes as unknown as IWfNodesLike | undefined);
  return map;
}

function derivedKeysFor(bpmnType: string): string[] {
  if (WF_STEP_TYPES.has(bpmnType)) return [...BASE_DERIVED_KEYS, ...STEP_DERIVED_KEYS];
  if (bpmnType === "bpmn:BoundaryEvent") return [...BASE_DERIVED_KEYS, ...BOUNDARY_DERIVED_KEYS];
  if (WF_STANDALONE_EVENT_TYPES.has(bpmnType)) return [...BASE_DERIVED_KEYS, ...EVENT_DERIVED_KEYS];
  if (WF_GATEWAY_TYPES.has(bpmnType)) return [...BASE_DERIVED_KEYS, ...GATEWAY_DERIVED_KEYS];
  if (bpmnType === "bpmn:SequenceFlow") return [...BASE_DERIVED_KEYS, ...FLOW_DERIVED_KEYS];
  return [...BASE_DERIVED_KEYS];
}

/** JSON-clean deep clone (drops undefined members and functions — the wire is pure JSON). */
function cloneJsonClean<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function buildSemantics(bpmnType: string, node: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!node) return {};
  const copy: Record<string, unknown> = { ...node };
  for (const key of derivedKeysFor(bpmnType)) {
    delete copy[key];
  }
  return cloneJsonClean(copy);
}

/** Non-array own fields of an IWFNodes bag (description, dueIn, ...) — docExtras/subprocessExtras. */
function nonArrayFields(bag: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!bag || typeof bag !== "object") return undefined;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(bag)) {
    const value: unknown = bag[key];
    if (value === undefined || Array.isArray(value)) continue;
    result[key] = value;
  }
  if (Object.keys(result).length === 0) return undefined;
  return cloneJsonClean(result);
}

// ---- geometry / topology helpers -----------------------------------------------------------------

function boundsOf(element: IWfRegistryElement): IWfBounds | undefined {
  if (
    typeof element.x !== "number" ||
    typeof element.y !== "number" ||
    typeof element.width !== "number" ||
    typeof element.height !== "number"
  ) {
    return undefined;
  }
  return { x: element.x, y: element.y, width: element.width, height: element.height };
}

function waypointsOf(element: IWfRegistryElement): IWfPoint[] | undefined {
  if (!Array.isArray(element.waypoints) || element.waypoints.length === 0) return undefined;
  return element.waypoints.map((point: IWfPoint): IWfPoint => ({ x: point.x, y: point.y }));
}

/** Nearest bpmn:SubProcess ancestor id, "" at the participant/process/root. Lanes are skipped. */
function containerIdOf(element: IWfRegistryElement): string {
  let current: IWfRegistryElement | undefined = element.parent;
  while (current) {
    if (current.type === "bpmn:SubProcess") return current.id;
    if (SKIPPED_CONTAINER_TYPES.has(current.type)) return "";
    current = current.parent;
  }
  return "";
}

/** flow-node id → lane id, from every lane's flowNodeRef list (first listing lane wins). */
function buildLaneMembership(elements: readonly IWfRegistryElement[]): Map<string, string> {
  const membership: Map<string, string> = new Map<string, string>();
  for (const element of elements) {
    if (element.type !== "bpmn:Lane" || element.labelTarget) continue;
    const refs: IWfModdleBusinessObject[] = element.businessObject?.flowNodeRef ?? [];
    for (const ref of refs) {
      if (typeof ref?.id === "string" && !membership.has(ref.id)) {
        membership.set(ref.id, element.id);
      }
    }
  }
  return membership;
}

// ---- the extractor --------------------------------------------------------------------------------

/**
 * Extracts the wf-patch document from the live modeler + semantic side-car. Returns null
 * when the modeler/registry is not ready or the workflow has no identity — the session
 * core then skips the step (join/diff) rather than seeding an empty doc.
 */
export function extractWfDocument(
  modeler: IWfModelerLike | undefined,
  selectedWf: IWorkFlow | undefined
): IWfDocument | null {
  if (!modeler || !selectedWf || !selectedWf.id) return null;
  let registry: IWfElementRegistryLike | undefined;
  try {
    registry = modeler.get("elementRegistry") as IWfElementRegistryLike | undefined;
  } catch {
    return null;
  }
  if (!registry || typeof registry.getAll !== "function") return null;

  const all: IWfRegistryElement[] = registry.getAll() ?? [];
  const definitions: IWfDefinitionsLike | undefined =
    typeof modeler.getDefinitions === "function" ? modeler.getDefinitions() : undefined;
  const prefixToUri: Map<string, string> = buildPrefixMap(definitions);
  const laneMembership: Map<string, string> = buildLaneMembership(all);
  const semanticNodes: Map<string, IWfSemanticEntry> = flattenSemanticNodes(selectedWf.nodes);

  const elements: Record<string, IWfElement> = {};
  /** containerId → { lanes-first, then flow elements } in registry traversal order. */
  const laneBuckets: Map<string, string[]> = new Map<string, string[]>();
  const memberBuckets: Map<string, string[]> = new Map<string, string[]>();
  let processId: string | undefined;
  let participantBounds: IWfBounds | undefined;

  const pushBucket = (buckets: Map<string, string[]>, containerId: string, id: string): void => {
    const bucket: string[] | undefined = buckets.get(containerId);
    if (bucket) {
      bucket.push(id);
    } else {
      buckets.set(containerId, [id]);
    }
  };

  for (const element of all) {
    if (!element || typeof element.id !== "string" || element.id === "") continue;
    if (element.type === "label" || element.labelTarget) continue;
    if (element.type === "bpmn:Process") {
      processId = element.id;
      continue;
    }
    if (element.type === "bpmn:Participant") {
      const refId: string | undefined = element.businessObject?.processRef?.id;
      if (typeof refId === "string" && refId !== "") processId = refId;
      // The participant stays OUT of the elements map, but its pool bounds are
      // user-mutable geometry and MUST replicate: bpmn-js grows lane and pool
      // together, so replicating only the lane leaves the flushed pool frozen
      // at the session seed (KOA-18258 "extra lane" corruption).
      participantBounds = boundsOf(element) ?? participantBounds;
      continue;
    }
    if (SKIPPED_CONTAINER_TYPES.has(element.type)) continue;
    if (!WF_SUPPORTED_BPMN_TYPES.has(element.type)) continue;
    if (elements[element.id] !== undefined) continue;

    const bo: IWfModdleBusinessObject = element.businessObject ?? {};
    const containerId: string = containerIdOf(element);
    const record: IWfElement = {
      id: element.id,
      bpmnType: element.type,
      containerId,
      semantics: buildSemantics(element.type, semanticNodes.get(element.id)?.node)
    };

    if (typeof bo.name === "string") record.name = bo.name;

    if (element.type === "bpmn:Group") {
      // The PERSISTED group label is categoryValueRef.value (bpmn-js updateLabel writes it
      // there); bo.name is only the interceptor's in-memory mirror. Prefer the persisted one.
      const categoryValue: string | undefined = bo.categoryValueRef?.value;
      if (typeof categoryValue === "string") record.name = categoryValue;
    }

    if (element.type === "bpmn:SequenceFlow") {
      const sourceId: string | undefined = element.source?.id ?? bo.sourceRef?.id;
      const targetId: string | undefined = element.target?.id ?? bo.targetRef?.id;
      if (typeof sourceId === "string") record.sourceId = sourceId;
      if (typeof targetId === "string") record.targetId = targetId;
      const waypoints: IWfPoint[] | undefined = waypointsOf(element);
      if (waypoints) record.waypoints = waypoints;
      const conditionXml: string | undefined = extractConditionExpressionXml(bo, prefixToUri);
      if (conditionXml !== undefined) record.conditionExpressionXml = conditionXml;
    } else {
      const bounds: IWfBounds | undefined = boundsOf(element);
      if (bounds) record.bounds = bounds;
    }

    if (element.type === "bpmn:BoundaryEvent") {
      const hostId: string | undefined = element.host?.id ?? bo.attachedToRef?.id;
      if (typeof hostId === "string") record.attachedToId = hostId;
      if (Object.prototype.hasOwnProperty.call(bo, "cancelActivity") && typeof bo.cancelActivity === "boolean") {
        record.cancelActivity = bo.cancelActivity;
      }
    }

    if (element.type === "bpmn:BoundaryEvent" || WF_STANDALONE_EVENT_TYPES.has(element.type)) {
      const eventDefinition: IWfEventDefinitionParts | null = extractEventDefinition(bo, prefixToUri);
      if (eventDefinition) {
        record.eventDefinition = eventDefinition.kind;
        record.eventDefinitionXml = eventDefinition.xml;
      }
    }

    if (WF_GATEWAY_TYPES.has(element.type)) {
      const defaultFlowId: string | undefined = bo.default?.id;
      if (typeof defaultFlowId === "string") record.defaultFlowId = defaultFlowId;
      if (element.di && typeof element.di.isMarkerVisible === "boolean") {
        record.isMarkerVisible = element.di.isMarkerVisible;
      }
    }

    if (element.type === "bpmn:SubProcess" && element.di && typeof element.di.isExpanded === "boolean") {
      record.isExpanded = element.di.isExpanded;
    }
    if (element.type === "bpmn:Lane" && element.di && typeof element.di.isHorizontal === "boolean") {
      record.isHorizontal = element.di.isHorizontal;
    }

    // DI identity + external label — labelBounds bijection: absent = no BPMNLabel,
    // null = label without bounds, object = label with bounds.
    record.shapeId = typeof element.di?.id === "string" && element.di.id !== "" ? element.di.id : `${element.id}_di`;
    const labelShape: IWfRegistryElement | undefined = element.label;
    const labelShapeBounds: IWfBounds | undefined = labelShape ? boundsOf(labelShape) : undefined;
    if (labelShapeBounds) {
      record.labelBounds = labelShapeBounds;
    } else if (element.di?.label) {
      const diLabelBounds: IWfBounds | undefined = element.di.label.bounds;
      record.labelBounds = diLabelBounds
        ? { x: diLabelBounds.x, y: diLabelBounds.y, width: diLabelBounds.width, height: diLabelBounds.height }
        : null;
    }
    if (record.labelBounds !== undefined && typeof element.di?.label?.id === "string" && element.di.label.id !== "") {
      record.labelId = element.di.label.id;
    }

    if (
      element.type !== "bpmn:Lane" &&
      element.type !== "bpmn:SequenceFlow" &&
      element.type !== "bpmn:Group" && // artifacts are never lane members
      containerId === ""
    ) {
      const laneId: string | undefined = laneMembership.get(element.id);
      if (laneId !== undefined) record.laneId = laneId;
    }

    const extensionXml: string | undefined = extractExtensionElementsXml(bo, prefixToUri);
    if (extensionXml !== undefined) record.extensionElementsXml = extensionXml;
    const documentationXml: string[] | undefined = extractDocumentationXml(bo, prefixToUri);
    if (documentationXml !== undefined) record.documentationXml = documentationXml;
    const customAttrs: IWfNsAttrOut[] | undefined = extractCustomAttrs(bo, prefixToUri);
    if (customAttrs !== undefined) record.customAttrs = customAttrs;
    if (element.type === "bpmn:Group") {
      // Declared moddle extension props (custom.json) live as typed bo fields, NOT in
      // $attrs — map them onto the same wire channel the server parses XML attrs into.
      const declared: IWfNsAttrOut[] = [];
      if (typeof bo.isHappyPathPhase === "boolean") {
        declared.push({
          ns: CUSTOM_MODDLE_NS,
          local: "isHappyPathPhase",
          value: bo.isHappyPathPhase ? "true" : "false"
        });
      }
      if (typeof bo.phaseId === "string" && bo.phaseId !== "") {
        declared.push({ ns: CUSTOM_MODDLE_NS, local: "phaseId", value: bo.phaseId });
      }
      if (declared.length > 0) {
        record.customAttrs = [...declared, ...(record.customAttrs ?? [])];
      }
    }

    if (element.type === "bpmn:SubProcess") {
      const semanticStep: Record<string, unknown> | undefined = semanticNodes.get(element.id)?.node;
      const subprocessExtras: Record<string, unknown> | undefined = nonArrayFields(
        semanticStep?.subprocess as Record<string, unknown> | undefined
      );
      if (subprocessExtras !== undefined) record.subprocessExtras = subprocessExtras;
    }

    elements[element.id] = record;
    if (element.type === "bpmn:Lane") {
      pushBucket(laneBuckets, containerId, element.id);
    } else {
      pushBucket(memberBuckets, containerId, element.id);
    }
  }

  const elementOrder: Record<string, string[]> = {};
  const bucketKeys: Set<string> = new Set<string>([...laneBuckets.keys(), ...memberBuckets.keys()]);
  for (const containerId of bucketKeys) {
    const merged: string[] = [...(laneBuckets.get(containerId) ?? []), ...(memberBuckets.get(containerId) ?? [])];
    if (merged.length > 0) elementOrder[containerId] = merged;
  }

  const doc: IWfDocument = {
    id: selectedWf.id,
    elements,
    elementOrder
  };
  if (processId !== undefined) doc.processId = processId;
  if (participantBounds !== undefined) doc.participantBounds = participantBounds;
  if (selectedWf.parameters !== undefined) doc.parameters = cloneJsonClean(selectedWf.parameters);
  if (selectedWf.happyPathDefinition !== undefined) {
    doc.happyPathDefinition = cloneJsonClean(selectedWf.happyPathDefinition);
  }
  const docExtras: Record<string, unknown> | undefined = nonArrayFields(
    selectedWf.nodes as unknown as Record<string, unknown> | undefined
  );
  if (docExtras !== undefined) doc.docExtras = docExtras;
  return doc;
}
